import { Trans, t } from '@lingui/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { MessageDescriptor } from '@lingui/core';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@shared/ui/breadcrumb';
import { Separator } from '@shared/ui/separator';
import { SidebarTrigger } from '@shared/ui/sidebar';
import { Button } from '@shared/ui/button';
import { Input } from '@shared/ui/input';
import { Badge } from '@shared/ui/badge';
import { ThemeToggle } from '@shared/ui/theme-toggle';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { Bell, Search, Settings, HelpCircle } from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import { getRoleHomePath, type NavItem } from '@shared/lib/navigation';
import { localizePath, useLocale } from '@shared/i18n';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';
import { CommandPalette } from '@shared/layout/CommandPalette';
import { NotificationCenter } from '@shared/layout/NotificationCenter';
import { useNotificationUnreadCount } from '@shared/hooks/notifications/useNotifications';

interface AppHeaderProps {
  user: UserProfile;
  currentPage?: NavItem;
  roleOverride?: UserProfile['role'];
  className?: string;
}

const getRoleDisplayName = (
  role: string,
  translate: (descriptor: MessageDescriptor) => string
): string => {
  switch (role) {
    case 'patient':
      return translate(
        msg({
          id: 'shared.layout.AppHeader.role.patient-portal',
          message: 'Patient Portal',
        })
      );
    case 'doctor':
      return translate(
        msg({
          id: 'shared.layout.AppHeader.role.doctor-dashboard',
          message: 'Doctor Dashboard',
        })
      );
    case 'admin':
      return translate(
        msg({
          id: 'shared.layout.AppHeader.role.admin-console',
          message: 'Admin Console',
        })
      );
    case 'super_admin':
      return translate(
        msg({
          id: 'shared.layout.AppHeader.role.system-administration',
          message: 'System Administration',
        })
      );
    default:
      return translate(
        msg({
          id: 'shared.layout.AppHeader.role.dashboard',
          message: 'Dashboard',
        })
      );
  }
};

export const AppHeader: React.FC<AppHeaderProps> = ({
  user,
  currentPage,
  roleOverride,
  className,
}) => {
  const { i18n } = useLingui();
  const { locale } = useLocale();
  const { resolveDoctorPath } = useDoctorWorkspace();
  const effectiveRole = roleOverride ?? user.role;
  const roleHomePath = resolveDoctorPath(getRoleHomePath(effectiveRole));
  const localizedRoleHomePath = localizePath(roleHomePath, locale);
  const roleDisplayName = getRoleDisplayName(effectiveRole, descriptor => i18n._(descriptor));
  const { data: unreadData } = useNotificationUnreadCount();
  const unreadCount = unreadData?.unreadCount ?? 0;
  const isStaff = effectiveRole === 'admin' || effectiveRole === 'super_admin';

  return (
    <header
      className={`flex h-16 shrink-0 items-center gap-2 border-b px-2 sm:px-4 md:rounded-tl-2xl ${className || ''}`}
    >
      <div className='flex items-center gap-2 min-w-0 flex-1'>
        <SidebarTrigger className='-ml-1' />
        <Separator orientation='vertical' className='mr-2 h-4 hidden sm:block' />
        <Breadcrumb className='min-w-0'>
          <BreadcrumbList>
            <BreadcrumbItem className='hidden md:block'>
              <BreadcrumbLink asChild>
                <Link to={localizedRoleHomePath} className='truncate'>
                  {roleDisplayName}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {currentPage && (
              <>
                <BreadcrumbSeparator className='hidden md:block' />
                <BreadcrumbItem className='min-w-0'>
                  <BreadcrumbPage className='flex items-center gap-2 min-w-0'>
                    <currentPage.icon className='size-4 flex-shrink-0' />
                    <span className='truncate'>
                      {typeof currentPage.label === 'string'
                        ? currentPage.label
                        : i18n._(currentPage.label as MessageDescriptor)}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className='flex items-center gap-1 sm:gap-2'>
        {/* Search - Hidden on mobile. Staff get the working command palette. */}
        {isStaff ? (
          <CommandPalette role={effectiveRole} />
        ) : (
          <div className='hidden lg:flex items-center gap-2'>
            <div className='relative'>
              <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                type='search'
                placeholder={t({
                  id: 'shared.layout.AppHeader.search_1',

                  message: 'Search...',
                })}
                className='w-[200px] xl:w-[300px] pl-8'
              />
            </div>
          </div>
        )}

        {/* Mobile search button */}
        <Button variant='ghost' size='icon' className='lg:hidden'>
          <Search className='h-4 w-4' />
          <span className='sr-only'>
            <Trans id='shared.layout.AppHeader.search'>Search</Trans>
          </span>
        </Button>

        {/* Action buttons */}
        <div className='flex items-center gap-1'>
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <NotificationCenter>
            <Button
              variant='ghost'
              size='icon'
              className='relative'
              aria-label={
                unreadCount > 0
                  ? `${t({ id: 'shared.layout.AppHeader.notifications-label', message: 'Notifications' })}, ${unreadCount} ${t({ id: 'shared.layout.AppHeader.unread-label', message: 'unread' })}`
                  : t({
                      id: 'shared.layout.AppHeader.notifications-label',
                      message: 'Notifications',
                    })
              }
            >
              <Bell className='h-4 w-4' aria-hidden='true' />
              {unreadCount > 0 ? (
                <Badge
                  aria-hidden='true'
                  variant='destructive'
                  className='absolute -top-1 -right-1 h-4 min-w-4 px-1 sm:h-5 sm:min-w-5 rounded-full p-0 text-xs flex items-center justify-center'
                >
                  {unreadCount > 99
                    ? t({
                        id: 'shared.layout.AppHeader.unread-count-99-plus',
                        message: '99+',
                      })
                    : unreadCount}
                </Badge>
              ) : null}
            </Button>
          </NotificationCenter>
          <span role='status' aria-live='polite' aria-atomic='true' className='sr-only'>
            {unreadCount > 0
              ? `${unreadCount} ${t({ id: 'shared.layout.AppHeader.unread-label', message: 'unread' })} ${t({ id: 'shared.layout.AppHeader.notifications-label', message: 'Notifications' })}`
              : ''}
          </span>

          {/* Help - Hidden on small mobile */}
          <Button variant='ghost' size='icon' className='hidden sm:flex'>
            <HelpCircle className='h-4 w-4' />
            <span className='sr-only'>
              <Trans id='shared.layout.AppHeader.help'>Help</Trans>
            </span>
          </Button>

          {/* Settings - Only for admin roles, hidden on small mobile */}
          {(effectiveRole === 'admin' || effectiveRole === 'super_admin') && (
            <Button variant='ghost' size='icon' className='hidden sm:flex'>
              <Settings className='h-4 w-4' />
              <span className='sr-only'>
                <Trans id='shared.layout.AppHeader.settings'>Settings</Trans>
              </span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
